import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MapPin, ShieldCheck, Wifi } from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import ProviderCard from "@/components/marketplace/ProviderCard";
import api from "@/lib/api";

const TYPES = [
  { key: "", label: "All" },
  { key: "local", label: "Local" },
  { key: "digital", label: "Digital" },
  { key: "hybrid", label: "Hybrid" },
];

const SORTS = [
  { key: "featured", label: "Featured first" },
  { key: "rating", label: "Highest rated" },
  { key: "jobs", label: "Most jobs" },
  { key: "recent", label: "Newest" },
];

export default function ProviderDirectoryPage() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [states, setStates] = useState([]);

  const q = sp.get("q") || "";
  const type = sp.get("type") || "";
  const state = sp.get("state") || "";
  const category_id = sp.get("category_id") || "";
  const remote = sp.get("remote") === "1";
  const verified = sp.get("verified") === "1";
  const sort = sp.get("sort") || "featured";

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/locations/states");
      setStates(data);
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const params = { limit: 24, sort };
        if (q) params.q = q;
        if (type) params.provider_type = type;
        if (category_id) params.category_id = category_id;
        if (remote) params.remote_only = true;
        if (verified) params.verified_only = true;
        // Note: filter by state_code -> we need a location_id. If state is
        // set, resolve to state location id first.
        if (state) {
          const { data: stRows } = await api.get(`/locations`, { params: { level: "state", state_code: state } });
          if (stRows[0]) params.location_id = stRows[0].id;
        }
        const { data } = await api.get("/providers", { params });
        setItems(data.items || []);
        setTotal(data.total || 0);
      } finally {
        setLoading(false);
      }
    })();
  }, [q, type, state, category_id, remote, verified, sort]);

  const update = (patch) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined || v === false) next.delete(k);
      else next.set(k, v === true ? "1" : v);
    });
    setSp(next, { replace: true });
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="mb-8">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Providers</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold text-[#071b22] tracking-tight" data-testid="providers-title">
            Find your Gulf Coast pro.
          </h1>
          <p className="mt-3 text-[#252A34]/75 max-w-2xl">
            {total.toLocaleString()} verified providers across FL, AL, MS, LA & TX.
          </p>
        </div>

        {/* Filter bar */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 mb-8 grid grid-cols-1 md:grid-cols-12 gap-3 items-center" data-testid="provider-filters">
          <label className="md:col-span-4 flex items-center gap-2 rounded-xl border border-slate-200 px-3 focus-within:border-[#165a71]">
            <Search className="h-4 w-4 text-[#165a71]" />
            <input
              type="text"
              value={q}
              onChange={(e) => update({ q: e.target.value })}
              placeholder="Search name, skill…"
              className="flex-1 py-2.5 outline-none bg-transparent text-sm"
              data-testid="providers-search"
            />
          </label>
          <label className="md:col-span-3 flex items-center gap-2 rounded-xl border border-slate-200 px-3 focus-within:border-[#165a71]">
            <MapPin className="h-4 w-4 text-[#165a71]" />
            <select
              value={state}
              onChange={(e) => update({ state: e.target.value })}
              className="flex-1 py-2.5 bg-transparent text-sm outline-none"
              data-testid="providers-state"
            >
              <option value="">Anywhere on the coast</option>
              {states.map((s) => (
                <option key={s.id} value={s.state_code}>{`${s.name} (${s.state_code})`}</option>
              ))}
            </select>
          </label>
          <div className="md:col-span-3 inline-flex items-center gap-1 rounded-full bg-slate-100 p-1">
            {TYPES.map((t) => (
              <button
                key={t.key || "all"}
                onClick={() => update({ type: t.key })}
                data-testid={`filter-type-${t.key || "all"}`}
                className={`flex-1 rounded-full px-3 py-2 text-xs font-medium transition-colors ${
                  (t.key || "") === (type || "") ? "bg-white text-[#071b22] shadow-sm" : "text-[#252A34]/70"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <select
            value={sort}
            onChange={(e) => update({ sort: e.target.value })}
            className="md:col-span-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm"
            data-testid="providers-sort"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>

          <div className="md:col-span-12 flex flex-wrap items-center gap-4 pt-1 border-t border-slate-100 mt-1">
            <label className="inline-flex items-center gap-2 text-sm text-[#252A34]/80">
              <input
                type="checkbox"
                checked={verified}
                onChange={(e) => update({ verified: e.target.checked })}
                data-testid="providers-verified-only"
                className="h-4 w-4 rounded border-slate-300 text-[#165a71] focus:ring-[#165a71]"
              />
              <ShieldCheck className="h-4 w-4 text-emerald-600" /> Verified only
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-[#252A34]/80">
              <input
                type="checkbox"
                checked={remote}
                onChange={(e) => update({ remote: e.target.checked })}
                data-testid="providers-remote-only"
                className="h-4 w-4 rounded border-slate-300 text-[#165a71] focus:ring-[#165a71]"
              />
              <Wifi className="h-4 w-4 text-[#165a71]" /> Remote-available
            </label>
            {category_id && (
              <button
                onClick={() => update({ category_id: "" })}
                data-testid="clear-category"
                className="ml-auto text-xs font-medium text-[#165a71] hover:underline"
              >
                Clear category filter
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-[#252A34]/60" data-testid="providers-loading">Loading providers…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center bg-white" data-testid="providers-empty">
            <div className="font-display text-xl text-[#071b22]">No providers match those filters yet.</div>
            <div className="text-[#252A34]/70 mt-2 text-sm">Try loosening a filter — or clear them all to see everyone.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {items.map((p, i) => (
              <ProviderCard key={p.id} provider={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
